import type { FetchQueryOptions, QueryClient } from '@tanstack/query-core';
import {
    NewsPageRequest,
    EventsPageRequest,
    FilingsPageRequest,
} from '@thinkalpha/quant-ws-client/contracts/time-list-page.js';
import type {
    NewsPageResponse,
    EventsPageResponse,
    FilingsPageResponse,
} from '@thinkalpha/quant-ws-client/contracts/time-list-page.js';
import type { RequestTicket, TimeListTransport } from '@thinkalpha/ui-symbol-tracked-time-list';
import { QueryClientOptions, shouldRetryTransportError } from 'src/lib/config/query-client';
import { quantWsClient } from 'src/lib/newQuant';

export function getNewsPageQuery({
    ticket,
    request,
}: {
    ticket: RequestTicket;
    request: NewsPageRequest;
}): FetchQueryOptions<NewsPageResponse> {
    return {
        queryKey: ['time-list', 'news', request.kind, ticket, NewsPageRequest.encode(request)],
        queryFn: ({ signal }) => quantWsClient.getNewsPage(request, signal),
        retry: shouldRetryTransportError,
        retryDelay: QueryClientOptions.defaultOptions!.queries!.retryDelay,
        staleTime: 0,
        gcTime: 0,
    };
}

export function getEventsPageQuery({
    ticket,
    request,
}: {
    ticket: RequestTicket;
    request: EventsPageRequest;
}): FetchQueryOptions<EventsPageResponse> {
    return {
        queryKey: ['time-list', 'events', request.kind, ticket, EventsPageRequest.encode(request)],
        queryFn: ({ signal }) => quantWsClient.getCorporateActionsPage(request, signal),
        retry: shouldRetryTransportError,
        retryDelay: QueryClientOptions.defaultOptions!.queries!.retryDelay,
        staleTime: 0,
        gcTime: 0,
    };
}

export function getFilingsPageQuery({
    ticket,
    request,
}: {
    ticket: RequestTicket;
    request: FilingsPageRequest;
}): FetchQueryOptions<FilingsPageResponse> {
    return {
        queryKey: ['time-list', 'filings', request.kind, ticket, FilingsPageRequest.encode(request)],
        queryFn: ({ signal }) => quantWsClient.getFilingsPage(request, signal),
        retry: shouldRetryTransportError,
        retryDelay: QueryClientOptions.defaultOptions!.queries!.retryDelay,
        staleTime: 0,
        gcTime: 0,
    };
}

/** Query owns retries/online pauses; each package ticket gets its own cancellable query. */
export function createTimeListTransport<Request, Response>(
    client: QueryClient,
    optionsFor: (call: { ticket: RequestTicket; request: Request }) => FetchQueryOptions<Response>,
): TimeListTransport<Request, Response> {
    return {
        async execute(call, signal) {
            signal.throwIfAborted();
            const options = optionsFor(call);
            const cancel = () => {
                void client.cancelQueries({ queryKey: options.queryKey, exact: true });
            };
            signal.addEventListener('abort', cancel, { once: true });
            try {
                return await client.fetchQuery(options);
            } finally {
                signal.removeEventListener('abort', cancel);
            }
        },
    };
}
